// Static skill tree definitions and a helper that derives `MetaModifiers`
// from the player's spent skill points. Read at game-scene start so the
// chosen perks affect the entire run.

import { save } from "./Save";

export interface SkillNode {
  id: string;            // unique key, also stored in save data
  trunk: "crown" | "forge" | "banner";
  name: string;
  description: string;
  maxRank: number;
  /** Pixel offsets within the trunk column (laid out in SkillTreeScene). */
  row: number;
  /** Optional prerequisite node id. Player must have rank >= 1 in that node. */
  requires?: string;
}

export const SKILL_NODES: SkillNode[] = [
  // CROWN — economy
  { id: "crown.startGold",  trunk: "crown", name: "Royal Treasury",  description: "+25 starting gold per rank.", maxRank: 8, row: 0 },
  { id: "crown.waveBonus",  trunk: "crown", name: "War Tribute",     description: "+10g extra per wave per rank.", maxRank: 8, row: 1 },
  { id: "crown.interest",   trunk: "crown", name: "Counting House",  description: "+0.6% interest per held 100g per rank.", maxRank: 6, row: 2, requires: "crown.waveBonus" },
  { id: "crown.sellRefund", trunk: "crown", name: "Frugal Salvage",  description: "+4% sell refund per rank.", maxRank: 6, row: 3 },
  { id: "crown.firstKill",  trunk: "crown", name: "Bounty Hunter",   description: "+1g per enemy killed per rank.", maxRank: 6, row: 4, requires: "crown.startGold" },

  // FORGE — towers
  { id: "forge.range",      trunk: "forge", name: "Long Sights",     description: "+3% range to all towers per rank.", maxRank: 8, row: 0 },
  { id: "forge.damage",     trunk: "forge", name: "Sharper Blades",  description: "+4% damage per rank.", maxRank: 8, row: 1 },
  { id: "forge.fireRate",   trunk: "forge", name: "Quick Hands",     description: "+3% fire rate per rank.", maxRank: 8, row: 2, requires: "forge.damage" },
  { id: "forge.discount",   trunk: "forge", name: "Master Builder",  description: "Cheaper tower construction per rank.", maxRank: 5, row: 3 },
  { id: "forge.upgradeBoost", trunk: "forge", name: "Honed Stones",  description: "Upgraded towers gain extra damage per rank.", maxRank: 8, row: 4, requires: "forge.range" },

  // BANNER — commander (unlocks abilities used in P14)
  { id: "banner.rally",     trunk: "banner", name: "Rally",          description: "Unlocks Rally: +30% fire rate for 8s.", maxRank: 1, row: 0 },
  { id: "banner.meteor",    trunk: "banner", name: "Meteor",         description: "Unlocks Meteor: heavy AoE at cursor.", maxRank: 1, row: 1, requires: "banner.rally" },
  { id: "banner.heal",      trunk: "banner", name: "Sanctuary",      description: "Unlocks Heal Castle: restore 5 HP.", maxRank: 1, row: 2, requires: "banner.rally" },
  { id: "banner.conscript", trunk: "banner", name: "Conscript",      description: "Unlocks Conscript: +200 gold immediately.", maxRank: 1, row: 3, requires: "banner.heal" },
  { id: "banner.faster",    trunk: "banner", name: "Battle Tempo",   description: "Reduced ability cooldowns per rank.", maxRank: 6, row: 4, requires: "banner.conscript" }
];

/** Aggregated multipliers/additives applied to a single run. */
export interface MetaModifiers {
  startGoldBonus: number;
  waveBonusGold: number;
  interestPctPer100: number;
  sellRefundPct: number;
  killBounty: number;

  rangeMult: number;
  damageMult: number;
  fireRateMult: number;
  buildCostMult: number;
  upgradeDamageMult: number; // applied per level above 1

  abilities: {
    rally: boolean;
    meteor: boolean;
    heal: boolean;
    conscript: boolean;
    cooldownMult: number;
  };
}

export function emptyMods(): MetaModifiers {
  return {
    startGoldBonus: 0,
    waveBonusGold: 0,
    interestPctPer100: 0,
    sellRefundPct: 0,
    killBounty: 0,
    rangeMult: 1,
    damageMult: 1,
    fireRateMult: 1,
    buildCostMult: 1,
    upgradeDamageMult: 1,
    abilities: { rally: false, meteor: false, heal: false, conscript: false, cooldownMult: 1 }
  };
}

export function computeMetaModifiers(): MetaModifiers {
  const spent = save.get().spentSkillPoints;
  const m = emptyMods();
  const r = (id: string): number => spent[id] ?? 0;

  m.startGoldBonus     = r("crown.startGold")  * 25;
  m.waveBonusGold      = r("crown.waveBonus")  * 10;
  m.interestPctPer100  = r("crown.interest")   * 0.006;
  m.sellRefundPct      = r("crown.sellRefund") * 0.04;
  m.killBounty         = r("crown.firstKill")  * 1;

  m.rangeMult         = 1 + r("forge.range")        * 0.03;
  m.damageMult        = 1 + r("forge.damage")       * 0.04;
  m.fireRateMult      = 1 + r("forge.fireRate")     * 0.03;
  m.buildCostMult     = 1 - r("forge.discount")     * 0.035;
  m.upgradeDamageMult = 1 + r("forge.upgradeBoost") * 0.035;

  m.abilities.rally        = r("banner.rally")     >= 1;
  m.abilities.meteor       = r("banner.meteor")    >= 1;
  m.abilities.heal         = r("banner.heal")      >= 1;
  m.abilities.conscript    = r("banner.conscript") >= 1;
  m.abilities.cooldownMult = 1 - r("banner.faster") * 0.07;
  return m;
}

export function nodeIsAvailable(node: SkillNode): boolean {
  if (!node.requires) return true;
  return (save.get().spentSkillPoints[node.requires] ?? 0) >= 1;
}

export function nodeRank(id: string): number {
  return save.get().spentSkillPoints[id] ?? 0;
}
